import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "components/ui/popover";
import { useSubstituteCandidatesQuery } from "@/services/regional-substitution";
import { IGerenteSummary } from "@/@types/regional-substitution";
import { useDebouncedValue } from "@/hooks/use-debounced-value";

interface UserComboboxProps {
  value?: string;
  onSelect: (id: string) => void;
  disabled?: boolean;
  fallback?: IGerenteSummary;
}

const ORDER_BY_NAME = JSON.stringify({ name: "asc" });

export function UserCombobox({
  value,
  onSelect,
  disabled = false,
  fallback,
}: UserComboboxProps) {
  const [open, setOpen] = useState<boolean>(false);
  const [search, setSearch] = useState<string>("");
  const debouncedSearch = useDebouncedValue(search, 300);

  const { data, isFetching } = useSubstituteCandidatesQuery(
    {
      search: debouncedSearch,
      orderBy: ORDER_BY_NAME,
      perPage: 50,
      meta: { page: 1 },
    },
    { skip: disabled },
  );

  const users: IGerenteSummary[] = data?.data ?? [];
  const fallbackSelected =
    fallback && fallback.id === value ? fallback : undefined;
  const selected = users.find((user) => user.id === value) ?? fallbackSelected;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label="Selecionar gerente substituto"
          className={cn(
            "w-full justify-between",
            !selected && "text-muted-foreground",
          )}
          disabled={disabled}
        >
          {selected ? selected.name : "Selecione o gerente substituto..."}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] max-w-[420px] p-0"
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Buscar por nome ou e-mail..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {isFetching ? (
              <div className="px-4 py-2 text-sm text-muted-foreground">
                Carregando usuários...
              </div>
            ) : (
              <>
                <CommandEmpty>Nenhum usuário encontrado.</CommandEmpty>
                <CommandGroup>
                  {users.map((user) => (
                    <CommandItem
                      key={user.id}
                      value={user.id}
                      onSelect={() => {
                        onSelect(user.id);
                        setOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          value === user.id ? "opacity-100" : "opacity-0",
                        )}
                      />
                      <span className="flex flex-col">
                        <span className="font-medium">{user.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {user.email}
                        </span>
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
